/**
 * Benchmark Model options for the dashboard sidebar.
 *
 * Ported from the legacy Vue app's `Modelitems` (SideBarDashboard/Cloud.vue).
 * The two values are hardcoded there too — there is no endpoint behind this
 * control — and the exact strings matter: downstream the legacy app compares
 * against 'General IT Average' to pick the Cost Advice variant and to choose
 * POST /cost-advice vs POST /scaler-cost-advice. Keep them byte-identical.
 */
import scalerIcon from '@/assets/benchmark/Scaler.svg';
import specIcon from '@/assets/benchmark/Spec.svg';

export const BENCHMARK_MODELS = Object.freeze({
  SPEC_CPU: 'SPEC CPU',
  GENERAL_IT_AVERAGE: 'General IT Average',
});

export const DEFAULT_BENCHMARK_MODEL = BENCHMARK_MODELS.SPEC_CPU;

/**
 * Per-model logos, copied from the legacy app's `assets/scaler/`.
 *
 * Legacy additionally applies `filter: brightness(0)` to the SPEC CPU mark,
 * which flattens it to solid black — right on its light Vuetify theme, but it
 * would make the icon invisible on this app's black surfaces. Not ported: both
 * marks are self-coloured (SPEC teal #09909F, Scaler green #34A853 with white
 * detail) and read correctly against the dark background as-is.
 */
export const BENCHMARK_MODEL_ICONS = Object.freeze({
  [BENCHMARK_MODELS.SPEC_CPU]: specIcon,
  [BENCHMARK_MODELS.GENERAL_IT_AVERAGE]: scalerIcon,
});

/**
 * The options this product exposes.
 *
 * 'General IT Average' is CCA-only — it drives a costing path EIA has no
 * backend for. The legacy app filters it the same way in `getModelItems()`,
 * so EIA legitimately renders a one-option select.
 *
 * @param {'cca' | 'eia'} mode
 * @returns {string[]}
 */
export function getBenchmarkModels(mode) {
  const all = [BENCHMARK_MODELS.SPEC_CPU, BENCHMARK_MODELS.GENERAL_IT_AVERAGE];
  return mode === 'cca' ? all : all.filter((m) => m !== BENCHMARK_MODELS.GENERAL_IT_AVERAGE);
}

/**
 * The upload row limit the selected model allows.
 *
 * Legacy renders this in the Manage Portfolio footer note and decides it
 * purely from `selectedModel` — 3K for General IT Average, 1M otherwise
 * (FooterActions.vue:11). No API is involved.
 *
 * @param {string} model
 * @returns {string} a display string, e.g. '1M'
 */
export function getUploadRowLimit(model) {
  return model === BENCHMARK_MODELS.GENERAL_IT_AVERAGE ? '3K' : '1M';
}
